import { useLocation } from 'react-router-dom';
import { useContext } from 'react';
import { breadcrumbConfig, matchRoute } from '@/config/breadcrumbs';

// Import context directly to avoid circular dependency
import React from 'react';

interface BreadcrumbContextType {
  labels: Record<string, string>;
  setLabel?: (path: string, label: string) => void;
}

const BreadcrumbContext = React.createContext<BreadcrumbContextType | null>(null);

export { BreadcrumbContext };

export interface Breadcrumb {
  label: string;
  path: string;
  isCurrent: boolean;
  icon?: string;
  prefix?: string;
}

/**
 * Hook to generate breadcrumb trail based on current route
 * Walks up the parent chain defined in breadcrumbConfig
 * Uses context for dynamic labels set by pages
 */
export function useBreadcrumbs(): Breadcrumb[] {
  const location = useLocation();
  const pathname = location.pathname;
  const context = useContext(BreadcrumbContext);
  const dynamicLabels = context?.labels || {};

  const breadcrumbs: Breadcrumb[] = [];
  let currentPath: string | undefined = pathname;
  // Carry params from deeper matches up the chain so we can substitute them
  // into parent patterns (e.g. `/sites/:siteId`) and produce real URLs for
  // dynamic-label lookups + link hrefs.
  let inheritedParams: Record<string, string> = {};

  // Walk up the parent chain
  while (currentPath) {
    const match = matchRoute(currentPath);

    if (!match) {
      break;
    }

    const config = breadcrumbConfig[match.pattern];
    inheritedParams = { ...inheritedParams, ...match.params };

    // Normalize to the matched pattern's canonical path (so leaf URLs like
    // /sites/:siteId/overview resolve to /sites/:siteId — both the dynamic
    // label registry and the link href use the canonical path).
    const canonicalPath = Object.entries(match.params).reduce(
      (p, [k, v]) => p.replace(`:${k}`, v),
      match.pattern,
    );

    let label = dynamicLabels[canonicalPath] || config.label;

    if (!dynamicLabels[canonicalPath]) {
      for (const [param, value] of Object.entries(match.params)) {
        label = label.replace(`:${param}`, value);
      }
    }

    breadcrumbs.unshift({
      label,
      path: canonicalPath,
      isCurrent: canonicalPath === pathname,
      icon: config.icon,
      prefix: config.prefix,
    });

    // Substitute carried params into the next parent pattern so the next
    // iteration matches a concrete URL, not the pattern string itself.
    currentPath = config.parent
      ? Object.entries(inheritedParams).reduce(
          (p, [k, v]) => p.replace(`:${k}`, v),
          config.parent,
        )
      : undefined;
  }

  return breadcrumbs;
}
